import React, { useState, useEffect, useRef } from 'react';
import { useApp } from '../context/AppContext';
import { triggerHaptic } from '../services/purrSynthesizer';
import {
  soundcheckSynth,
  calculateStagePower,
  applyTungstenSoftRamp,
  X32,
  buildEnttecProDmxPacket,
  buildArtNetDmxPacket,
  StageFixtureInfo
} from '../services/stageProtocol';
import { sendUsbCommand, sendUsbDmx, isUsbSerialSupported } from '../services/usbSerial';
import { pollNfcTag, BastetNative } from '../services/bastetBridge';
import { resolveStageAsset, StageAsset } from '../services/stageAssetDb';
import {
  Sliders,
  Flame,
  ShieldAlert,
  Volume2,
  VolumeX,
  Radio,
  Zap,
  Power,
  RefreshCw,
  Play,
  Square,
  Activity,
  Wifi,
  Usb,
  Cpu,
  Eye,
  AlertTriangle,
  Music,
  Maximize2
} from 'lucide-react';

export const StageRigDeckView: React.FC = () => {
  const { profile } = useApp();

  // Mode: Standalone Direct (USB-OTG/Direct UDP) vs Hub Relay (echodedicated)
  const [operationalMode, setOperationalMode] = useState<'standalone' | 'hub'>('standalone');
  const [activeTab, setActiveTab] = useState<'lighting' | 'sound' | 'soundcheck' | 'nfc'>('lighting');
  const [nightVision, setNightVision] = useState(false);

  // Master Safety State
  const [isDeadBlackout, setIsDeadBlackout] = useState(false);
  const [grandMaster, setGrandMaster] = useState(100); // 0-100%
  const [preheatEnabled, setPreheatEnabled] = useState(true);

  // Guarded DBO Press-and-Hold State (Inviolable Guardrail #5: >=600ms hold)
  const [dboHoldProgress, setDboHoldProgress] = useState(0);
  const [dboTooltip, setDboTooltip] = useState<string | null>(null);
  const dboHoldTimerRef = useRef<NodeJS.Timeout | null>(null);
  const dboHoldStartRef = useRef<number>(0);

  // Lighting Fixtures Inventory (Guardian Productions Rig defaults)
  const [fixtures, setFixtures] = useState<StageFixtureInfo[]>([
    { name: 'Altman Leko 360Q #1', type: 'altman_360q_500w', wattsPerUnit: 500, count: 1, currentLevel: 80, channel: 1 },
    { name: 'Altman Leko 360Q #2', type: 'altman_360q_500w', wattsPerUnit: 500, count: 1, currentLevel: 80, channel: 2 },
    { name: 'Altman Leko 360Q #3', type: 'altman_360q_500w', wattsPerUnit: 500, count: 1, currentLevel: 60, channel: 3 },
    { name: 'Altman Leko 360Q #4', type: 'altman_360q_500w', wattsPerUnit: 500, count: 1, currentLevel: 60, channel: 4 },
    { name: 'Par 64 Narrow Spots', type: 'par64_500w', wattsPerUnit: 500, count: 2, currentLevel: 40, channel: 5 },
    { name: 'DMX RGB Wash Bank', type: 'led_rgb_par', wattsPerUnit: 120, count: 4, currentLevel: 70, channel: 7 },
    { name: '750W Stage Strobe', type: 'strobe_750w', wattsPerUnit: 750, count: 1, currentLevel: 0, channel: 11 },
  ]);

  // Special Effects (Fogger)
  const [fogActive, setFogActive] = useState(false);
  const [fogCountdown, setFogCountdown] = useState(0);
  const fogTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Sound Console (Behringer X32) State
  const [x32Connected, setX32Connected] = useState(true);
  const [x32HeartbeatTick, setX32HeartbeatTick] = useState(0);
  const [mainFader, setMainFader] = useState(0.75); // 0.0 - 1.0 (0.75 ~ 0dB)
  const [mainMuted, setMainMuted] = useState(false);
  const [dcaFaders, setDcaFaders] = useState([
    { id: 1, name: 'DCA 1: Drums', val: 0.70, muted: false },
    { id: 2, name: 'DCA 2: Bass/Gtr', val: 0.75, muted: false },
    { id: 3, name: 'DCA 3: Vocals', val: 0.80, muted: false },
    { id: 4, name: 'DCA 4: Horns/FX', val: 0.65, muted: false },
  ]);

  // Soundcheck Audio Metrology
  const [synthPlaying, setSynthPlaying] = useState<string | null>(null);
  const [synthVolume, setSynthVolume] = useState(0.25);

  // NFC Asset Tag Inspector
  const [nfcScanning, setNfcScanning] = useState(false);
  const [scannedTag, setScannedTag] = useState<{ uid: string; name?: string; channel?: number; details?: string } | null>(null);

  // Electrical load calculation
  const powerMetrics = calculateStagePower(isDeadBlackout ? fixtures.map(f => ({ ...f, currentLevel: 0 })) : fixtures);

  // Keep X32 /xremote heartbeat pulsing every 6 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setX32HeartbeatTick((t) => (t + 1) % 100);
    }, 6000);
    return () => clearInterval(interval);
  }, []);

  // Listen for live X32 OSC telemetry from Gateway
  useEffect(() => {
    const handler = (e: any) => {
      const event = e.detail;
      if (!event) return;
      if (event.type === 'x32_fader') {
        if (event.channel === 'main') {
          setMainFader(event.level);
        } else {
          setDcaFaders((prev) =>
            prev.map((f) => (f.id === event.channel ? { ...f, val: event.level } : f))
          );
        }
      } else if (event.type === 'x32_mute') {
        if (event.channel === 'main') {
          setMainMuted(event.muted);
        } else {
          setDcaFaders((prev) =>
            prev.map((f) => (f.id === event.channel ? { ...f, muted: event.muted } : f))
          );
        }
      }
    };

    window.addEventListener('stage_event' as any, handler);
    return () => window.removeEventListener('stage_event' as any, handler);
  }, []);

  // Handler: Change fixture fader
  const handleFixtureLevelChange = (channel: number, newLevel: number) => {
    setFixtures((prev) =>
      prev.map((f) => (f.channel === channel ? { ...f, currentLevel: newLevel } : f))
    );
    // If USB Serial is attached and in standalone mode, transmit direct DMX
    if (operationalMode === 'standalone') {
      const dmxUniverse = new Uint8Array(512);
      fixtures.forEach((fix) => {
        const effectiveLevel = isDeadBlackout ? 0 : Math.round((fix.currentLevel * (grandMaster / 100)));
        const dmxByte = applyTungstenSoftRamp(0, effectiveLevel, preheatEnabled);
        if (fix.channel <= 512) {
          dmxUniverse[fix.channel - 1] = dmxByte;
        }
      });
      // Fire packet asynchronously via MCU-timed Enttec Pro USB framing
      if (isUsbSerialSupported()) {
        sendUsbDmx(dmxUniverse).catch(() => {});
      }
    }
  };

  // Guarded DBO Handlers (Press & hold >=600ms)
  const startDboHold = () => {
    dboHoldStartRef.current = Date.now();
    setDboTooltip(null);
    if (dboHoldTimerRef.current) clearInterval(dboHoldTimerRef.current);

    dboHoldTimerRef.current = setInterval(() => {
      const elapsed = Date.now() - dboHoldStartRef.current;
      const progress = Math.min(100, Math.round((elapsed / 600) * 100));
      setDboHoldProgress(progress);
      if (progress >= 100) {
        if (dboHoldTimerRef.current) clearInterval(dboHoldTimerRef.current);
        dboHoldTimerRef.current = null;
        triggerHaptic([60, 50, 100]);
        setIsDeadBlackout((prev) => !prev);
        setDboHoldProgress(0);
      }
    }, 20);
  };

  const endDboHold = () => {
    if (dboHoldTimerRef.current) {
      clearInterval(dboHoldTimerRef.current);
      dboHoldTimerRef.current = null;
      if (dboHoldProgress > 0 && dboHoldProgress < 100) {
        setDboTooltip('⚠️ Guarded DBO: Press & hold for 600ms to toggle blackout');
        setTimeout(() => setDboTooltip(null), 3500);
      }
    }
    setDboHoldProgress(0);
  };

  // Handler: Fog Machine Momentary Trigger (Max 3s safety)
  const triggerFogBurst = () => {
    if (fogActive) return;
    triggerHaptic(40);
    setFogActive(true);
    setFogCountdown(3);

    let secondsLeft = 3;
    fogTimerRef.current = setInterval(() => {
      secondsLeft -= 1;
      setFogCountdown(secondsLeft);
      if (secondsLeft <= 0) {
        if (fogTimerRef.current) clearInterval(fogTimerRef.current);
        setFogActive(false);
        triggerHaptic(20);
      }
    }, 1000);
  };

  // Handler: Soundcheck Tones
  const handleToggleTone = (type: 'pink' | '1khz' | '100hz') => {
    triggerHaptic(30);
    if (synthPlaying === type) {
      soundcheckSynth.stop();
      setSynthPlaying(null);
    } else {
      if (type === 'pink') {
        soundcheckSynth.startPinkNoise(synthVolume);
      } else if (type === '1khz') {
        soundcheckSynth.startSineTone(1000, synthVolume);
      } else if (type === '100hz') {
        soundcheckSynth.startSineTone(100, synthVolume);
      }
      setSynthPlaying(type);
    }
  };

  // Handler: NFC Scan for Stage Cases (Resolved against Guardian Rig DB)
  const handleScanCaseTag = async () => {
    setNfcScanning(true);
    triggerHaptic(30);
    try {
      const res = await pollNfcTag(4000);
      if (res.tagFound && res.tag) {
        triggerHaptic([30, 40, 50]);
        const asset = resolveStageAsset(res.tag.uid);
        if (asset) {
          setScannedTag({
            uid: res.tag.uid || 'UNKNOWN_UID',
            name: asset.name,
            channel: asset.channel,
            details: `${asset.specs} • ${asset.connector} • Lamp: ${asset.lamp || 'N/A'}`
          });
        } else {
          setScannedTag({
            uid: res.tag.uid || 'UNKNOWN_UID',
            name: 'Guardian Tag Interrogated',
            channel: 1,
            details: `Tag UID: ${res.tag.uid} • Ready for asset assignment.`
          });
        }
      } else {
        setScannedTag(null);
      }
    } catch {
      setScannedTag(null);
    } finally {
      setNfcScanning(false);
    }
  };

  return (
    <div className={`flex-1 overflow-y-auto px-3.5 py-3 space-y-3.5 font-sans pb-16 transition-colors duration-300 ${
      nightVision
        ? 'bg-[#0a0500] text-amber-200 selection:bg-amber-900'
        : 'bg-slate-950 text-slate-100'
    }`}>
      {/* Top Stage Header & Operational Mode Badge */}
      <div className="rounded-2xl p-3.5 bg-gradient-to-r from-red-950/40 via-amber-950/30 to-slate-900 border border-amber-500/30 shadow-2xl flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-xl bg-amber-500/20 border border-amber-400/40 flex items-center justify-center text-amber-300 shadow-quantum-glow">
            <Flame className="w-5 h-5" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h2 className="font-cinzel text-xs font-bold text-amber-300 tracking-wider">
                STAGE RIG & FIELD CONTROL
              </h2>
              <span className="text-[9px] px-1.5 py-0.5 rounded font-mono font-bold bg-amber-500/20 text-amber-300 border border-amber-500/40">
                GUARDIAN RIG
              </span>
            </div>
            <p className="text-[10px] text-slate-400 font-mono">
              Altman Lekos • Behringer X32 • DMX512
            </p>
          </div>
        </div>

        {/* Mode Switcher & Night Vision Filter */}
        <div className="flex items-center gap-1.5">
          <button
            onClick={() => {
              triggerHaptic(25);
              setNightVision((v) => !v);
            }}
            className={`px-2 py-1 rounded-xl flex items-center gap-1 border text-[10px] font-mono transition-all ${
              nightVision
                ? 'bg-amber-500/30 text-amber-300 border-amber-400 font-bold shadow-md'
                : 'bg-black/40 text-slate-400 border-white/10 hover:text-white'
            }`}
            title="Night Vision Filter (Monochrome Amber)"
          >
            <Eye className="w-3 h-3" />
            <span>{nightVision ? 'NV Amber' : 'Day UI'}</span>
          </button>

          <div className="flex items-center bg-black/40 rounded-xl p-1 border border-white/10 text-[10px] font-mono">
            <button
              onClick={() => {
                triggerHaptic(20);
                setOperationalMode('standalone');
              }}
              className={`px-2.5 py-1 rounded-lg flex items-center gap-1 transition-all ${
                operationalMode === 'standalone'
                  ? 'bg-amber-500 text-slate-950 font-bold shadow-md'
                  : 'text-slate-400 hover:text-white'
              }`}
            >
              <Usb className="w-3 h-3" />
              Direct Edge
            </button>
            <button
              onClick={() => {
                triggerHaptic(20);
                setOperationalMode('hub');
              }}
              className={`px-2.5 py-1 rounded-lg flex items-center gap-1 transition-all ${
                operationalMode === 'hub'
                  ? 'bg-red-600 text-white font-bold shadow-md'
                  : 'text-slate-400 hover:text-white'
              }`}
            >
              <Wifi className="w-3 h-3" />
              Hub Relay
            </button>
          </div>
        </div>
      </div>

      {/* Emergency Control: Dead Blackout & Grand Master & Circuit Load HUD */}
      <div className={`rounded-2xl p-3.5 border transition-all ${
        isDeadBlackout
          ? 'bg-red-950/80 border-red-500 shadow-[0_0_30px_rgba(239,68,68,0.4)]'
          : 'bg-slate-900/90 border-slate-800'
      }`}>
        <div className="flex items-center justify-between gap-3">
          {/* Dead Blackout Button (Guarded >=600ms hold) */}
          <div className="flex-1 flex flex-col">
            <button
              onPointerDown={startDboHold}
              onPointerUp={endDboHold}
              onPointerLeave={endDboHold}
              className={`relative overflow-hidden py-3 px-3 rounded-xl font-bold font-cinzel text-xs flex items-center justify-center gap-2 border select-none transition-all ${
                isDeadBlackout
                  ? 'bg-red-600 hover:bg-red-500 text-white border-red-400 shadow-quantum-glow animate-pulse'
                  : 'bg-red-950/40 hover:bg-red-900/60 text-red-300 border-red-500/40'
              }`}
            >
              {/* Hold progress bar fill */}
              {dboHoldProgress > 0 && !isDeadBlackout && (
                <div
                  className="absolute inset-0 bg-red-600/70 transition-all duration-75"
                  style={{ width: `${dboHoldProgress}%` }}
                />
              )}
              {isDeadBlackout && dboHoldProgress > 0 && (
                <div
                  className="absolute inset-0 bg-emerald-600/70 transition-all duration-75"
                  style={{ width: `${dboHoldProgress}%` }}
                />
              )}
              <Power className="w-4 h-4 relative z-10" />
              <span className="relative z-10">
                {isDeadBlackout
                  ? (dboHoldProgress > 0 ? `HOLD TO RESTORE (${dboHoldProgress}%)` : 'BLACKOUT LATCHED (HOLD TO RESTORE)')
                  : (dboHoldProgress > 0 ? `LATCHING DBO (${dboHoldProgress}%)` : 'DEAD BLACKOUT (HOLD 600ms)')}
              </span>
            </button>
            {dboTooltip && (
              <div className="text-[10px] text-amber-400 mt-1 text-center font-mono animate-fade-in">
                {dboTooltip}
              </div>
            )}
          </div>

          {/* Grand Master Fader Gauge */}
          <div className="flex items-center gap-2 bg-black/50 px-3 py-1.5 rounded-xl border border-white/10">
            <Sliders className="w-4 h-4 text-amber-400" />
            <div className="text-right">
              <div className="text-[9px] text-slate-400 font-mono">GRAND MASTER</div>
              <div className="text-xs font-bold text-amber-300 font-mono">{grandMaster}%</div>
            </div>
            <input
              type="range"
              min="0"
              max="100"
              value={grandMaster}
              onChange={(e) => setGrandMaster(Number(e.target.value))}
              className="w-16 h-1.5 bg-slate-700 rounded-lg accent-amber-400 cursor-pointer"
            />
          </div>
        </div>

        {/* Electrical Metrology Bar */}
        <div className="mt-3 pt-2.5 border-t border-white/10 flex items-center justify-between text-[11px] font-mono">
          <div className="flex items-center gap-3">
            <span className="text-slate-400">Total Rig Load:</span>
            <span className="font-bold text-white">{powerMetrics.totalWatts} W</span>
            <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${
              powerMetrics.exceeds20A
                ? 'bg-red-600 text-white animate-bounce'
                : powerMetrics.exceeds15A
                ? 'bg-amber-500 text-black'
                : 'bg-emerald-950 text-emerald-300 border border-emerald-500/40'
            }`}>
              {powerMetrics.totalAmps} A @ 120V ({powerMetrics.percentOf20A}% of 20A)
            </span>
          </div>

          <label className="flex items-center gap-1.5 cursor-pointer text-slate-300 text-[10px]">
            <input
              type="checkbox"
              checked={preheatEnabled}
              onChange={(e) => setPreheatEnabled(e.target.checked)}
              className="accent-amber-400 rounded"
            />
            5% Tungsten Preheat (Inrush Protection)
          </label>
        </div>
      </div>

      {/* Navigation Tabs for Sub-Rigs */}
      <div className="flex gap-1.5 bg-slate-900/60 p-1 rounded-xl border border-white/10">
        <button
          onClick={() => setActiveTab('lighting')}
          className={`flex-1 py-1.5 rounded-lg text-xs font-semibold flex items-center justify-center gap-1.5 transition-all ${
            activeTab === 'lighting'
              ? 'bg-amber-500/20 text-amber-300 border border-amber-500/40'
              : 'text-slate-400 hover:text-white'
          }`}
        >
          <Flame className="w-3.5 h-3.5" />
          Lighting
        </button>

        <button
          onClick={() => setActiveTab('sound')}
          className={`flex-1 py-1.5 rounded-lg text-xs font-semibold flex items-center justify-center gap-1.5 transition-all ${
            activeTab === 'sound'
              ? 'bg-amber-500/20 text-amber-300 border border-amber-500/40'
              : 'text-slate-400 hover:text-white'
          }`}
        >
          <Music className="w-3.5 h-3.5" />
          X32 Console
        </button>

        <button
          onClick={() => setActiveTab('soundcheck')}
          className={`flex-1 py-1.5 rounded-lg text-xs font-semibold flex items-center justify-center gap-1.5 transition-all ${
            activeTab === 'soundcheck'
              ? 'bg-amber-500/20 text-amber-300 border border-amber-500/40'
              : 'text-slate-400 hover:text-white'
          }`}
        >
          <Activity className="w-3.5 h-3.5" />
          Soundcheck
        </button>

        <button
          onClick={() => setActiveTab('nfc')}
          className={`flex-1 py-1.5 rounded-lg text-xs font-semibold flex items-center justify-center gap-1.5 transition-all ${
            activeTab === 'nfc'
              ? 'bg-amber-500/20 text-amber-300 border border-amber-500/40'
              : 'text-slate-400 hover:text-white'
          }`}
        >
          <Radio className="w-3.5 h-3.5" />
          Case Tag
        </button>
      </div>

      {/* TAB 1: LIGHTING RIG (Altman Lekos & Pars) */}
      {activeTab === 'lighting' && (
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-2.5">
            {fixtures.map((fixture) => {
              const effectiveLevel = isDeadBlackout ? 0 : Math.round(fixture.currentLevel * (grandMaster / 100));
              const isTungsten = fixture.type.includes('360q') || fixture.type.includes('par64');

              return (
                <div
                  key={fixture.channel}
                  className="rounded-xl p-3 bg-slate-900/70 border border-slate-800 flex flex-col justify-between"
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="font-semibold text-xs text-white flex items-center gap-1">
                        {fixture.name}
                        {isTungsten && <span className="text-[9px] text-amber-400">⚡</span>}
                      </div>
                      <div className="text-[10px] text-slate-400 font-mono">
                        DMX Ch {fixture.channel} • {fixture.wattsPerUnit}W
                      </div>
                    </div>
                    <span className="font-mono text-xs font-bold text-amber-300 bg-amber-500/15 px-1.5 py-0.5 rounded">
                      {effectiveLevel}%
                    </span>
                  </div>

                  {/* Level Slider */}
                  <div className="mt-3">
                    <input
                      type="range"
                      min="0"
                      max="100"
                      value={fixture.currentLevel}
                      onChange={(e) => handleFixtureLevelChange(fixture.channel, Number(e.target.value))}
                      className="w-full h-2 bg-slate-800 rounded-lg accent-amber-400 cursor-pointer"
                    />
                  </div>

                  {/* Quick Preset Buttons */}
                  <div className="flex gap-1.5 mt-2.5 text-[10px] font-mono">
                    <button
                      onClick={() => handleFixtureLevelChange(fixture.channel, 0)}
                      className="flex-1 py-1 rounded bg-white/5 hover:bg-white/10 text-slate-400"
                    >
                      OFF
                    </button>
                    <button
                      onClick={() => handleFixtureLevelChange(fixture.channel, 50)}
                      className="flex-1 py-1 rounded bg-white/5 hover:bg-white/10 text-slate-300"
                    >
                      50%
                    </button>
                    <button
                      onClick={() => handleFixtureLevelChange(fixture.channel, 100)}
                      className="flex-1 py-1 rounded bg-amber-500/20 hover:bg-amber-500/30 text-amber-300 font-bold"
                    >
                      FULL
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Atmosphere & Special Effects Panel */}
          <div className="rounded-xl p-3 bg-gradient-to-r from-purple-950/30 to-slate-900 border border-purple-500/30 flex items-center justify-between">
            <div>
              <div className="font-bold text-xs text-purple-300 flex items-center gap-1.5">
                <Zap className="w-4 h-4 text-purple-400" />
                HIGH-CAPACITY FOG MACHINE
              </div>
              <div className="text-[10px] text-slate-400 font-mono">
                Momentary Safe Trigger • Auto-Off Bounded
              </div>
            </div>

            <button
              onClick={triggerFogBurst}
              disabled={fogActive}
              className={`py-2 px-4 rounded-xl font-bold font-mono text-xs flex items-center gap-1.5 transition-all ${
                fogActive
                  ? 'bg-purple-600 text-white animate-pulse'
                  : 'bg-purple-950/60 hover:bg-purple-900/80 text-purple-200 border border-purple-500/40'
              }`}
            >
              <Zap className="w-3.5 h-3.5" />
              {fogActive ? `FOGGING (${fogCountdown}s)` : '3s FOG BURST'}
            </button>
          </div>
        </div>
      )}

      {/* TAB 2: SOUND CONSOLE (Behringer X32) */}
      {activeTab === 'sound' && (
        <div className="space-y-3">
          {/* X32 Heartbeat HUD */}
          <div className="rounded-xl p-3 bg-slate-900/80 border border-slate-800 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-2.5 h-2.5 rounded-full bg-emerald-400 animate-ping" />
              <div>
                <div className="font-bold text-xs text-white font-mono">BEHRINGER X32 REMOTE ENGINE</div>
                <div className="text-[10px] text-slate-400 font-mono">UDP :10023 • Heartbeat: Active</div>
              </div>
            </div>
            <span className="text-[10px] text-emerald-400 bg-emerald-500/10 px-2 py-0.5 rounded border border-emerald-500/30 font-mono">
              /xremote SYNC
            </span>
          </div>

          {/* Main Stereo LR Fader */}
          <div className="rounded-xl p-3 bg-slate-900/80 border border-slate-800 flex items-center justify-between">
            <div>
              <div className="font-bold text-xs text-amber-300 font-mono">MAIN STEREO LR BUS</div>
              <div className="text-[10px] text-slate-400 font-mono">
                Master Front-of-House Output
              </div>
            </div>

            <div className="flex items-center gap-3">
              <button
                onClick={() => {
                  triggerHaptic(25);
                  setMainMuted(!mainMuted);
                }}
                className={`px-3 py-1.5 rounded-lg text-xs font-bold font-mono transition-all ${
                  mainMuted
                    ? 'bg-red-600 text-white'
                    : 'bg-slate-800 text-slate-300 hover:text-white'
                }`}
              >
                {mainMuted ? 'MUTED' : 'MUTE'}
              </button>
              <div className="flex items-center gap-2">
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.01"
                  value={mainFader}
                  onChange={(e) => {
                    const nextVal = Number(e.target.value);
                    if (Math.abs(nextVal - 0.75) < 0.02 && Math.abs(mainFader - 0.75) >= 0.02) {
                      triggerHaptic([30, 20]); // 0 dB detent notch tick
                    }
                    setMainFader(nextVal);
                  }}
                  className="w-28 sm:w-36 h-3 bg-slate-700 rounded-lg accent-amber-400 cursor-pointer"
                />
                <span className="text-xs font-mono font-bold text-white w-12 text-right">
                  {Math.abs(mainFader - 0.75) < 0.02 ? '0 dB' : `${Math.round(mainFader * 100)}%`}
                </span>
              </div>
            </div>
          </div>

          {/* DCA Subgroup Faders */}
          <div className="grid grid-cols-2 gap-2.5">
            {dcaFaders.map((dca) => (
              <div key={dca.id} className="rounded-xl p-3 bg-slate-900/70 border border-slate-800">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-bold text-xs text-white font-mono">{dca.name}</span>
                  <button
                    onClick={() => {
                      triggerHaptic(20);
                      setDcaFaders((prev) =>
                        prev.map((d) => (d.id === dca.id ? { ...d, muted: !d.muted } : d))
                      );
                    }}
                    className={`px-2 py-0.5 rounded text-[10px] font-mono font-bold ${
                      dca.muted ? 'bg-red-600 text-white' : 'bg-slate-800 text-slate-400'
                    }`}
                  >
                    {dca.muted ? 'MUTE' : 'ON'}
                  </button>
                </div>
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.01"
                  value={dca.val}
                  onChange={(e) => {
                    const newVal = Number(e.target.value);
                    setDcaFaders((prev) =>
                      prev.map((d) => (d.id === dca.id ? { ...d, val: newVal } : d))
                    );
                  }}
                  className="w-full h-2 bg-slate-800 rounded-lg accent-amber-400 cursor-pointer"
                />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* TAB 3: SOUNDCHECK & ACOUSTIC METROLOGY */}
      {activeTab === 'soundcheck' && (
        <div className="space-y-3">
          <div className="rounded-xl p-3 bg-slate-900/80 border border-slate-800">
            <h3 className="font-bold text-xs text-amber-300 font-cinzel mb-1">
              ACOUSTIC ROOM CALIBRATION GENERATOR
            </h3>
            <p className="text-[11px] text-slate-400 mb-3">
              Generate reference tones directly from Echo's phone DAC into the stage board for RTA room calibration and feedback ringing.
            </p>

            {/* Volume Control */}
            <div className="flex items-center gap-3 mb-3 bg-black/40 p-2.5 rounded-lg border border-white/5">
              <Volume2 className="w-4 h-4 text-amber-400" />
              <span className="text-[10px] font-mono text-slate-400">Generator Gain:</span>
              <input
                type="range"
                min="0.05"
                max="0.8"
                step="0.05"
                value={synthVolume}
                onChange={(e) => setSynthVolume(Number(e.target.value))}
                className="flex-1 h-1.5 bg-slate-700 rounded-lg accent-amber-400 cursor-pointer"
              />
              <span className="text-xs font-mono font-bold text-white">{Math.round(synthVolume * 100)}%</span>
            </div>

            {/* Tone Selector Buttons */}
            <div className="grid grid-cols-3 gap-2">
              <button
                onClick={() => handleToggleTone('pink')}
                className={`py-2.5 px-2 rounded-xl text-xs font-mono font-bold flex flex-col items-center gap-1 border transition-all ${
                  synthPlaying === 'pink'
                    ? 'bg-amber-500 text-black border-amber-400 shadow-quantum-glow animate-pulse'
                    : 'bg-white/5 border-white/10 text-slate-300 hover:text-white'
                }`}
              >
                <Activity className="w-4 h-4" />
                <span>PINK NOISE</span>
                <span className="text-[9px] opacity-75 font-normal">1/f RTA Curve</span>
              </button>

              <button
                onClick={() => handleToggleTone('1khz')}
                className={`py-2.5 px-2 rounded-xl text-xs font-mono font-bold flex flex-col items-center gap-1 border transition-all ${
                  synthPlaying === '1khz'
                    ? 'bg-amber-500 text-black border-amber-400 shadow-quantum-glow animate-pulse'
                    : 'bg-white/5 border-white/10 text-slate-300 hover:text-white'
                }`}
              >
                <Play className="w-4 h-4" />
                <span>1 kHz SINE</span>
                <span className="text-[9px] opacity-75 font-normal">0 dBu Ref</span>
              </button>

              <button
                onClick={() => handleToggleTone('100hz')}
                className={`py-2.5 px-2 rounded-xl text-xs font-mono font-bold flex flex-col items-center gap-1 border transition-all ${
                  synthPlaying === '100hz'
                    ? 'bg-amber-500 text-black border-amber-400 shadow-quantum-glow animate-pulse'
                    : 'bg-white/5 border-white/10 text-slate-300 hover:text-white'
                }`}
              >
                <Volume2 className="w-4 h-4" />
                <span>100 Hz SUB</span>
                <span className="text-[9px] opacity-75 font-normal">Subwoofer Test</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* TAB 4: NFC CASE TAGS */}
      {activeTab === 'nfc' && (
        <div className="space-y-3">
          <div className="rounded-xl p-4 bg-slate-900/80 border border-slate-800 text-center">
            <Radio className="w-8 h-8 text-amber-400 mx-auto mb-2 animate-bounce" />
            <h3 className="font-bold text-xs text-white font-cinzel">NTAG21x HARDWARE ASSET SCANNER</h3>
            <p className="text-[11px] text-slate-400 max-w-xs mx-auto mb-4">
              Tap phone to an NFC coin tag mounted on Guardian road cases or lighting trees to instantly identify fixture profiles and DMX addressing.
            </p>

            <button
              onClick={handleScanCaseTag}
              disabled={nfcScanning}
              className={`py-2.5 px-6 rounded-xl font-bold font-mono text-xs inline-flex items-center gap-2 border transition-all ${
                nfcScanning
                  ? 'bg-amber-500/30 text-amber-200 border-amber-400 animate-pulse'
                  : 'bg-amber-500 text-black border-amber-400 hover:bg-amber-400'
              }`}
            >
              <Radio className="w-4 h-4" />
              {nfcScanning ? 'SCANNING AIRSPACE...' : 'SCAN CASE TAG'}
            </button>
          </div>

          {scannedTag && (
            <div className="rounded-xl p-3 bg-gradient-to-r from-amber-950/40 to-slate-900 border border-amber-500/40 shadow-lg">
              <div className="flex items-center justify-between mb-1.5">
                <span className="font-bold text-xs text-amber-300">{scannedTag.name}</span>
                <span className="text-[9px] font-mono text-slate-400">UID: {scannedTag.uid}</span>
              </div>
              <p className="text-[11px] text-slate-300 font-mono mb-2">{scannedTag.details}</p>
              <button
                onClick={() => {
                  triggerHaptic(20);
                  setActiveTab('lighting');
                }}
                className="w-full py-1.5 rounded-lg bg-amber-500/20 hover:bg-amber-500/30 border border-amber-500/40 text-amber-300 text-xs font-mono font-bold"
              >
                SELECT & FOCUS CHANNELS 1-4 IN LIGHTING DECK →
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
